export default function initGfProgramOfInterest() {
  window.GfProgramOfInterestField = class GfProgramOfInterestField {

    static programsEndpoint = gfPoiFieldSettings.apiDomain + "/googlespreadsheet/find/type"

    // Get configuration value for localStorage expiration
    static localStorageExpirationMilliseconds = gfPoiFieldSettings.dataExpirationHours * 60 * 60 * 1000

    // Grab data from localStorage if available
    static programData =  false

    // Key to store program data under in localStorage
    static localStorageDataKey = 'cc-program-of-interest-data'

    static validProgramTypes = ['programs','tapprograms','tradprograms']

    // Stores Promises from fetch action so if there are more than one POI field
    // on the page they will all wait on the first fetch() call before trying
    // to populate their select list.
    // (Only needed if data of this.#type is not in localStorage yet.)
    static programDataFetchPromise = {}

    // This instance's <select> element
    #selectElement

    // This instance's hidden input element
    #hiddenInput

    // Type of programs that are selectable in this field
    #type 
    
    // TODO: Need to support some options passed via constructor for 
    // program type (trad, cco etc) and other POI specific config/filters
    constructor(fieldId, programType, show){

      // Only support a subset of types from forms API
      if(!GfProgramOfInterestField.validProgramTypes.includes(programType)){
        
        console.cclog('Invalid programType, ("' + programType + '"),' +
           'passed to GfProgramOfInterestField(...), defaulting to "tradprograms".')

        programType = 'tradprograms'
      }

      // We need to have a program type or the API call fails, default to trad
      // if we are given an empty string.
      this.#type = programType

      this.#selectElement = document.getElementById(fieldId)
      this.#hiddenInput = document.getElementById(fieldId + '_hidden')

      // Make sure we found the right element for this POI field
      if(this.#selectElement && this.#hiddenInput){
        
        console.cclog('Initializing GfProgramOfInterestField for #' + fieldId)

        // Display the POI field if forcePoiVisible is true
        if (gfPoiFieldSettings.forcePoiVisible) {
          const gfield = this.#selectElement.closest('.gfield');
          if (gfield) {
            gfield.classList.remove('poi-hidden');
          }
        }

        // Indicate options are not present yet
        this.#selectElement.disabled = true

        // Update hidden input when select element changes
        this.#selectElement.addEventListener('change', () => {
          this.#hiddenInput.value = this.#selectElement.value;
        });

        // Populate this field with programs, handle as promise incase we 
        // need to wait on a pending fetch() action
        this.initProgramData().then(()=> this.populate())
        
      } else {

        console.cclog('ERROR: GfProgramOfInterestField could not find field by ID attribute #' + fieldId)
      }
    }

    async initProgramData() {

      // Check to see if we have the data already
      // TODO invalidate when too old
      GfProgramOfInterestField.programData = JSON.parse(localStorage.getItem(GfProgramOfInterestField.localStorageDataKey))

      let programDataExpired = false

      // If data is stored, and there is a timestamp for this data type
      if(GfProgramOfInterestField.programData && GfProgramOfInterestField.programData[this.#type]){

        let dataTimestamp = GfProgramOfInterestField.programData[this.#type + '_timestamp']

        if(dataTimestamp){
          
          programDataExpired = (Date.now() - dataTimestamp) >= GfProgramOfInterestField.localStorageExpirationMilliseconds
        }

      }
      
      // Fetch data for this field if:
      //   - we don't have programData  
      //   - we have program data but the requsted type of programs is missing
      //   - we've hit the expiration age for programData in localStorage
      //
      if(!GfProgramOfInterestField.programData || 
        !GfProgramOfInterestField.programData[this.#type] || 
        programDataExpired){
        
        // Only fetch once, track the fetch action for multiple POI fields
        // that might appear in-page.
        if(!GfProgramOfInterestField.programDataFetchPromise[this.#type]){
          
          // Get program JSON
          GfProgramOfInterestField.programDataFetchPromise[this.#type] = fetch(
              GfProgramOfInterestField.programsEndpoint + '/' + this.#type
            ).then(
              (response) => response.json()
            ).then((data) => {
              
              // If no program data in localStorage, create object first
              if(!GfProgramOfInterestField.programData){
                GfProgramOfInterestField.programData = {}
              } 

              // Add type of data to programData w/timestamp
              GfProgramOfInterestField.programData[this.#type] = data.message
              GfProgramOfInterestField.programData[this.#type + '_timestamp'] = Date.now() 

              // Store in localStorage
              localStorage.setItem(
                GfProgramOfInterestField.localStorageDataKey,
                JSON.stringify(GfProgramOfInterestField.programData))
            }
          )
        }
      
        // Return the existing fetch() Promise so that 
        // all field instances know when data is ready
        return GfProgramOfInterestField.programDataFetchPromise[this.#type]
      } else {

        // No need to wait data is fetched and in localStorage
        return Promise.resolve()
      }
    }


    // Populates options within select field when called with 
    // program data of this.#type
    populate() {
      
      let programs = GfProgramOfInterestField.programData[this.#type]

      // Clear options if needed
      for (const option of this.#selectElement.options) {
        option.remove();
      }

      // Only include active programs
      programs = programs.filter(program => program.active == "TRUE");

      // If program offerings, show only those options.
      if (Array.isArray(gfPoiFieldSettings.offeringProgramCodes) && gfPoiFieldSettings.offeringProgramCodes.length) {
        const offeringCodes = new Set(gfPoiFieldSettings.offeringProgramCodes);
        programs = programs.filter(program => offeringCodes.has(program.code));
      }
      
      // Need to sort alpha
      // then include undeclared after
      programs.sort((a, b) => {
        const titleA = a.display_title.toUpperCase();
        const titleB = b.display_title.toUpperCase();
      
        const isUndeclaredA = titleA.startsWith("UNDECLARED");
        const isUndeclaredB = titleB.startsWith("UNDECLARED");
      
        if (isUndeclaredA && !isUndeclaredB) {
          return 1; // a should come after b
        } else if (!isUndeclaredA && isUndeclaredB) {
          return -1; // a should come before b
        } else {
          // If both are "Undeclared" or both are not "Undeclared", compare alphabetically
          return titleA.localeCompare(titleB);
        }
      });

      let placeholder = document.createElement("option");
      placeholder.text = 'Select a Major';
      placeholder.value = '';
      this.#selectElement.add(placeholder);

      for (let i = 0; i < programs.length; i++) {
        
        let entry = document.createElement("option");

        entry.text = programs[i].display_title;
        entry.value = programs[i].code;

        this.#selectElement.add(entry);
      }

      // Make sure field is usable
      this.#selectElement.disabled = false

      // Check if there's a value in the hidden input
      if (this.#hiddenInput.value) {
        this.#selectElement.value = this.#hiddenInput.value;
      }
      // Otherwise, check if the current page's program code/forms_api_code exists 
      // as an option in this field?
      else if (gfPoiFieldSettings.currentProgramCode && 
        GfProgramOfInterestField.programData[this.#type].find(
          (e)=> (e.code == gfPoiFieldSettings.currentProgramCode)
      )){
        this.#selectElement.value = gfPoiFieldSettings.currentProgramCode;
        this.#hiddenInput.value = gfPoiFieldSettings.currentProgramCode;
        console.cclog("GfProgramOfInterestField (" + this.#type + ") POI preset to: "+ gfPoiFieldSettings.currentProgramCode);
      }
    }

  }

  // Run after Gravity Forms and the multiple form instances plugin finish rendering/updating IDs
  jQuery(document).on('gform_post_render', function() {
    // console.cclog('ℹ️ gform_post_render for gf-program-of-interest.js')
    initializeProgramOfInterestFields();
  });
  
  function initializeProgramOfInterestFields() {
    // console.cclog('ℹ️ initializeProgramOfInterestFields for gf-program-of-interest.js')
    // Find all selects that look like entry term fields
    document.querySelectorAll('.gfield--type-program_of_interest .gfield_select').forEach(select => {
      // console.cclog('ℹ️ Found select element #' + select.id)
      // Avoid double-initializing (optional: add a data attribute after init)
      if (!select.dataset.gfProgramOfInterestInitialized) {
        // new window.GfProgramOfInterestField(select.id);
        const programType = select.dataset.ccProgramApiType;
        new window.GfProgramOfInterestField(select.id, programType);
        select.dataset.gfProgramOfInterestInitialized = 'true';
      }
    });
    // console.cclog('All program of interest select fields:', document.querySelectorAll('.gfield--type-program_of_interest .gfield_select'));
  }

}